/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Posting to a cooking group.
 *
 * ── WHAT THIS REPLACED ──────────────────────────────────────────────────────
 * Three stacked form controls, always expanded, in this order: a monospace
 * "suggested title" input with a paragraph of explanatory text under it, then a
 * textarea, then a photo button. The first thing you met when you wanted to say
 * "that looks great" was a labelled field for naming a dish, in a monospace
 * font, with instructions.
 *
 * ── THE ORDER IS THE FIX ────────────────────────────────────────────────────
 * Collapsed, it is one line: "Share what you're cooking". Tapping it opens the
 * caption box and the photo button together, because those two are what a post
 * actually is. Naming a dish is a secondary, optional refinement and now sits
 * behind a small toggle where an optional thing belongs.
 *
 * ── PHOTOS ARE THE POINT, SO THEY GO FIRST ──────────────────────────────────
 * The photo button sits beside the caption rather than under it, and a post
 * with photos and no words is explicitly allowed (the server has always
 * permitted this — `postMessage` treats a photo as a complete message).
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { CirclesApi } from "../../api/endpoints.ts";
import type { AvailableLibraryRecipe } from "../../api/types.ts";
import { downscaleImage } from "../../lib/downscaleImage.ts";
import { ErrorBox } from "../../components/ui.tsx";
import { Icon } from "../../components/Icon.tsx";
import { useToast } from "../../components/Toast.tsx";
import { usePrefs } from "../../i18n/index.tsx";
import { canSendPost, tagPickerShelf, tagPostFields } from "./recipeTag.ts";

/** Mirrors MAX_PHOTOS_PER_MESSAGE on the server and the CHECK in migration 030. */
const MAX_PHOTOS = 4;

export function Composer({ circleId, canPost, lockedReason, onManage }: {
  circleId: string;
  canPost: boolean;
  lockedReason?: string;
  /** Opens the Manage-sharing sheet — the picker's remedy when sharing is off. */
  onManage: () => void;
}) {
  const { t } = usePrefs();
  const qc = useQueryClient();
  const toast = useToast();

  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [dish, setDish] = useState("");
  const [showDish, setShowDish] = useState(false);
  const [tag, setTag] = useState<AvailableLibraryRecipe | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [photos, setPhotos] = useState<{ dataUrl: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The tag picker reads the same shelf as the SharedRecipeStrip — own shares
  // included — so the two never disagree about what the group can see.
  const shelfQuery = useQuery({
    queryKey: ["circle-library-shelf", circleId],
    queryFn: () => CirclesApi.library.recipes.list(circleId, true),
    retry: false,
    enabled: open,
  });
  const sharingQuery = useQuery({
    queryKey: ["circle-library-settings", circleId],
    queryFn: () => CirclesApi.library.settings.get(circleId),
    retry: false,
    enabled: open,
  });
  const shelf = tagPickerShelf(
    shelfQuery.data?.recipes ?? [],
    sharingQuery.data?.enabled ?? false,
  );

  const post = useMutation({
    mutationFn: () => CirclesApi.postMessage(circleId, {
      body: body.trim(),
      ...tagPostFields(tag, dish),
      photos: photos.length ? photos.map((p) => p.dataUrl) : undefined,
    }),
    onSuccess: () => {
      setBody(""); setDish(""); setShowDish(false); setTag(null); setPickerOpen(false);
      setPhotos([]); setError(null);
      setOpen(false);
      void qc.invalidateQueries({ queryKey: ["circle-messages", circleId] });
      void qc.invalidateQueries({ queryKey: ["circle", circleId] });
    },
    onError: (e: Error) => toast(e.message, "danger"),
  });

  const addPhotos = async (files: FileList | null) => {
    if (!files?.length) return;
    setBusy(true);
    setError(null);
    try {
      const room = MAX_PHOTOS - photos.length;
      const picked = [...files].slice(0, Math.max(0, room));
      if (files.length > room) {
        setError(t("circles.photoTooMany").replace("{max}", String(MAX_PHOTOS)));
      }
      // Sequential, not Promise.all: decoding four full-size camera images at
      // once will stall the main thread on a phone. Downscaling happens in the
      // browser because a 12MP photo is ~4MB and the column CHECK caps a stored
      // photo at 2MB — an un-downscaled upload is a guaranteed rejection after
      // a long wait on mobile data.
      const out: { dataUrl: string }[] = [];
      for (const file of picked) {
        const { dataUrl } = await downscaleImage(file);
        out.push({ dataUrl });
      }
      setPhotos((xs) => [...xs, ...out].slice(0, MAX_PHOTOS));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't read that image.");
    }
    setBusy(false);
  };

  const canSend = canPost && !post.isPending && canSendPost(body, photos.length);
  const pickerEmpty = shelf.group.length === 0 && shelf.own.length === 0;

  const pickerRow = (r: AvailableLibraryRecipe) => (
    <button
      key={r.recipeId}
      type="button"
      className="flex w-full items-center gap-2 rounded-md px-1 py-1.5 text-left text-sm hover:bg-accent/10"
      onClick={() => { setTag(r); setPickerOpen(false); }}
    >
      {r.imageUrl ? (
        <img src={r.imageUrl} alt="" className="h-7 w-7 shrink-0 rounded-md object-cover" />
      ) : (
        <Icon name="book" className="h-4 w-4 shrink-0 text-ink-3" />
      )}
      <span className="min-w-0 flex-1 truncate">{r.title}</span>
      <span className="shrink-0 text-xs text-ink-3">{r.fromHousehold}</span>
    </button>
  );

  if (!canPost) {
    return (
      <div className="card p-4 text-center text-sm text-ink-3">
        {lockedReason ?? t("circles.cannotPost")}
      </div>
    );
  }

  // ── collapsed ─────────────────────────────────────────────────────────────
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="card flex w-full items-center gap-3 p-4 text-left transition hover:border-accent/50"
      >
        <span aria-hidden className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-accent/15 text-accent">
          <Icon name="camera" className="h-4 w-4" />
        </span>
        <span className="text-sm text-ink-3">Share what you're cooking…</span>
        <Icon name="plus" className="ml-auto h-4 w-4 shrink-0 text-ink-3" />
      </button>
    );
  }

  // ── open ──────────────────────────────────────────────────────────────────
  return (
    <div className="card space-y-3 p-4">
      <textarea
        className="input min-h-[5rem] resize-none"
        placeholder="How did it go? What would you change?"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        maxLength={1000}
        autoFocus
        aria-label="What you want to say"
      />

      {photos.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {photos.map((p, i) => (
            <div key={p.dataUrl.slice(-32) + i} className="relative">
              <img src={p.dataUrl} alt=""
                   className="h-20 w-20 rounded-lg border border-rule-soft object-cover" />
              <button
                type="button"
                className="absolute -right-2 -top-2 grid h-6 w-6 place-items-center rounded-full bg-ink text-paper"
                onClick={() => setPhotos((xs) => xs.filter((_, j) => j !== i))}
                aria-label={t("circles.removePhoto")}
              >
                <Icon name="x" className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Naming a dish: optional, and behind a toggle. It used to be the first
          and most prominent field in the composer. A recipe tag is the OTHER
          way to identify the dish — one identity per post, so a selected tag
          dims this field. */}
      {showDish && (
        <label className={`block ${tag ? "opacity-50" : ""}`}>
          <span className="text-xs font-medium text-ink-2">What's the dish called?</span>
          <input
            className="input mt-1"
            placeholder="Slow-roast lamb"
            value={dish}
            onChange={(e) => setDish(e.target.value)}
            maxLength={200}
            disabled={!!tag}
            autoFocus
          />
          {/* 027 is deliberate that this is a name, not a share. Saying so here
              prevents someone believing they've sent the recipe. */}
          <span className="mt-1 block text-xs text-ink-3">
            Just the name — it doesn't share the recipe itself.
          </span>
        </label>
      )}

      {/* The recipe tag: a pointer at a recipe already shared with this circle
          through library sharing (029). Selected → a removable chip. Picking →
          the shelf's two sections. Otherwise just the toggle, beside
          "+ Name the dish". */}
      {tag ? (
        <div className="flex items-center gap-2 rounded-lg border border-rule-soft px-2 py-1.5">
          {tag.imageUrl ? (
            <img src={tag.imageUrl} alt="" className="h-8 w-8 shrink-0 rounded-md object-cover" />
          ) : (
            <span aria-hidden className="grid h-8 w-8 shrink-0 place-items-center rounded-md bg-accent/15 text-accent">
              <Icon name="book" className="h-4 w-4" />
            </span>
          )}
          <span className="min-w-0 flex-1 truncate text-sm">{tag.title}</span>
          <button
            type="button"
            onClick={() => setTag(null)}
            aria-label={t("common.remove")}
            className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-ink text-paper"
          >
            <Icon name="x" className="h-3 w-3" />
          </button>
        </div>
      ) : pickerOpen ? (
        <div className="space-y-2 rounded-lg border border-rule-soft p-2">
          {shelfQuery.isPending ? (
            <div className="px-1 py-1.5 text-xs text-ink-3">…</div>
          ) : pickerEmpty ? (
            <div className="flex items-center justify-between gap-2 px-1 py-1.5">
              <span className="text-xs text-ink-3">{t("circles.tagEmpty")}</span>
              <button type="button" onClick={onManage}
                      className="shrink-0 text-xs text-accent hover:underline">
                {t("circles.manageSharing")}
              </button>
            </div>
          ) : (
            <>
              {shelf.group.length > 0 && (
                <div>
                  <div className="px-1 text-xs font-medium text-ink-2">
                    {t("circles.tagPickerGroup")}
                  </div>
                  {shelf.group.map(pickerRow)}
                </div>
              )}
              {shelf.ownSharingOn ? (
                shelf.own.length > 0 && (
                  <div>
                    <div className="px-1 text-xs font-medium text-ink-2">
                      {t("circles.tagPickerOwn")}
                    </div>
                    {shelf.own.map(pickerRow)}
                  </div>
                )
              ) : (
                <div className="flex items-center justify-between gap-2 px-1 py-1.5">
                  <span className="text-xs text-ink-3">{t("circles.tagSharingOff")}</span>
                  <button type="button" onClick={onManage}
                          className="shrink-0 text-xs text-accent hover:underline">
                    {t("circles.manageSharing")}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <div className="flex gap-4">
          {!showDish && (
            <button type="button" className="text-xs text-accent hover:underline"
                    onClick={() => setShowDish(true)}>
              + Name the dish
            </button>
          )}
          <button type="button" className="text-xs text-accent hover:underline"
                  onClick={() => setPickerOpen(true)}>
            {t("circles.tagRecipe")}
          </button>
        </div>
      )}

      {error && <ErrorBox message={error} />}

      <div className="flex items-center gap-2">
        <label className={`btn-ghost !py-1.5 text-xs ${
          busy || photos.length >= MAX_PHOTOS ? "cursor-not-allowed opacity-50" : "cursor-pointer"}`}>
          <Icon name="camera" className="h-4 w-4" />
          {busy ? t("circles.photoProcessing") : "Photo"}
          <input
            type="file" accept="image/*" multiple className="hidden"
            disabled={busy || photos.length >= MAX_PHOTOS}
            onChange={(e) => { void addPhotos(e.target.files); e.target.value = ""; }}
          />
        </label>
        <span className="text-xs text-ink-3">{photos.length}/{MAX_PHOTOS}</span>

        <button className="btn-ghost ml-auto !py-1.5" onClick={() => setOpen(false)}>
          Cancel
        </button>
        <button className="btn-primary !py-1.5" disabled={!canSend}
                onClick={() => post.mutate()}>
          {post.isPending ? "Posting…" : "Post"}
        </button>
      </div>
    </div>
  );
}
